import {
  createContext,
  useReducer,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import classnames from "classnames";
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from "body-scroll-lock";
import { useRouter } from "next/router";
import { useModal } from "@/hooks";
import isOuterAreaClick from "@/lib/isOuterAreaClick";
import styles from "@/styles/Modal.module.scss";
import IconClose from "../svgs/icon_close.svg";

export const ModalContext = createContext({
  showModal: () => null,
  hideModal: () => null,
});
ModalContext.displayName = "ModalContext";

let id = 0;
function generateId() {
  return id++;
}

const initialValue = [];
const reducer = (state, action) => {
  const newState = [...state];
  switch (action.type) {
    case "add":
      newState.push(action.value);
      return newState;
    case "remove":
      newState.pop();
      return newState;
    default:
      return state;
  }
};

export function ModalProvider({ children }) {
  const [modals, dispatch] = useReducer(reducer, initialValue);

  const showModal = useCallback((modalComponent, modalProps = {}) => {
    dispatch({
      type: "add",
      value: {
        component: modalComponent,
        props: modalProps,
        key: `modal-${generateId()}`,
      },
    });
  }, []);

  const hideModal = useCallback(() => {
    dispatch({ type: "remove" });
  }, []);

  const contextValue = useMemo(
    () => ({
      showModal,
      hideModal,
    }),
    []
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modals.map((modal) => (
        <Modal key={modal.key} {...modal.props}>
          {modal.component}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
}

export default function Modal({
  style = "medium",
  closeButton = true,
  outerAreaClose = true,
  onClose,
  children,
}) {
  const modalStyles = useMemo(() => {
    if (typeof style === "string") {
      return styles[style];
    }
    if (Array.isArray(style)) {
      return style
        .map((s) => {
          return styles[s];
        })
        .join(" ");
    }
  }, [style]);

  const screen = useRef();
  const content = useRef();
  const { hideModal } = useModal();
  const router = useRouter();

  useEffect(() => {
    disableBodyScroll(screen.current, {
      allowTouchMove: (el) => {
        return el.tagName === "DIV" || el.tagName === "SPAN";
      },
    });
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  const close = useCallback((e) => {
    if (onClose) {
      onClose(e);
    }
    enableBodyScroll(screen.current);
    hideModal();
  }, []);

  const onScreenClick = useCallback((e) => {
    if (!outerAreaClose) return false;
    if (isOuterAreaClick(content.current, e)) {
      close(e);
    }
  });

  useEffect(() => {
    router.events.on("routeChangeComplete", close);
    return () => {
      router.events.off("routeChangeComplete", close);
    };
  }, [close]);

  return (
    <div className={styles.modal} ref={screen} onClick={onScreenClick}>
      <div className={classnames(styles.content, modalStyles)} ref={content}>
        {closeButton && (
          <button
            className={styles.close}
            tabIndex="0"
            role="button"
            onClick={close}
          >
            <IconClose />
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
